import { TEXT_PROPS } from '@/constants/text';
import { DEFAULT_SHAPES, SHAPE_PROPS } from '@/constants/shapes';
import { IMAGE_PROPS } from '@/constants/media';
import { LAYER_PROPS } from '@/constants/layers';

export const COLOR_KEYS = new Set<string>([
  TEXT_PROPS.FILL,
  SHAPE_PROPS.FILL,
  SHAPE_PROPS.STROKE,
  IMAGE_PROPS.SHADOW_COLOR
]);

/**
 * Target key of a Font Size % column: a percent of each size's own font size,
 * bound to the layer's `fontSize`, so every ad size keeps its own tuning.
 */
export const FONT_SCALE_KEY = 'fontScale';

/** Target key of a Corner Radius % column, bound to `borderRadius` the same way. */
export const RADIUS_SCALE_KEY = 'radiusScale';

/** Target key of a Shape column: a preset name, bound to the shape's `path`. */
export const SHAPE_KEY = 'shape';

/** Plain-number target keys and the lowest value each accepts. */
export const NUMBER_KEY_MIN: Readonly<Record<string, number>> = {
  [SHAPE_PROPS.STROKE_WIDTH]: 0,
  [LAYER_PROPS.ANGLE]: Number.NEGATIVE_INFINITY,
  [FONT_SCALE_KEY]: 1,
  [RADIUS_SCALE_KEY]: 0
};

/** Pick-from-a-list target keys and the values the grid dropdown offers. */
export const CHOICE_KEY_OPTIONS: Readonly<Record<string, readonly string[]>> = {
  [TEXT_PROPS.FONT_WEIGHT]: [
    '100',
    '200',
    '300',
    '400',
    '500',
    '600',
    '700',
    '800',
    '900'
  ],
  [TEXT_PROPS.FONT_STYLE]: ['normal', 'italic'],
  [SHAPE_KEY]: DEFAULT_SHAPES.map(shape => shape.name)
};

/** Words a font weight cell may hold instead of a number. */
export const FONT_WEIGHT_ALIASES: Readonly<Record<string, number>> = {
  normal: 400,
  bold: 700
};

export const BOOLEAN_STRINGS = new Set(['true', 'false']);

export const ATTRIBUTE_VALUE_TYPES = {
  STRING: 'string',
  BOOLEAN: 'boolean',
  NUMBER: 'number',
  CHOICE: 'choice',
  URL: 'url',
  VIDEO_URL: 'videoUrl',
  AUDIO_URL: 'audioUrl',
  COLOR: 'color',
  OPACITY: 'opacity',
  LANGUAGE: 'language',
  PRODUCT_ID: 'product_id',
  FONT_FAMILY: 'fontFamily'
} as const;

/**
 * Target key of a Language column, and the property a text layer binds it to.
 * Deliberately not in `TEXT_PROPS`: a language is never painted onto the layer,
 * so keeping it out of there keeps it out of `TEXT_FABRIC_KEYS` too.
 */
export const LANGUAGE_KEY = 'language';

/** Target key of a Product ID column (a SKU per row); never painted, like Language. */
export const PRODUCT_ID_KEY = 'product_id';

export const FONT_FAMILY_KEY = TEXT_PROPS.FONT_FAMILY;

